import type { SampleRuleConditions } from "@unbox/api";
import { useMemo } from "react";
import { useSampleRulePreview } from "@/api/sample-rules";
import { ImpactPreviewBanner } from "@/components/blocklist/rules/impact-preview-banner";
import { useDebounce } from "@/hooks/use-debounce";
import { isConditionComplete } from "../condition-fields";

const isConditionsComplete = (conditions: SampleRuleConditions) =>
  [...conditions.when, ...conditions.except].every(
    (group) => group.conditions.length > 0 && group.conditions.every(isConditionComplete),
  );

interface ImpactPreviewProps {
  conditions: SampleRuleConditions;
}

export function ImpactPreview({ conditions }: ImpactPreviewProps) {
  // Debounce the serialised tree, not the object: useDebounce compares its input by
  // reference, and the tree arrives from react-hook-form's useWatch, which is free to
  // hand back a fresh object each render. Debouncing the reference would restart the
  // timer on every render instead of only when an operator or value actually changes.
  const serialised = JSON.stringify(conditions);
  const debouncedSerialised = useDebounce(serialised, 400);
  const debouncedConditions = useMemo(
    () => JSON.parse(debouncedSerialised) as SampleRuleConditions,
    [debouncedSerialised],
  );

  const preview = useSampleRulePreview(isConditionsComplete(debouncedConditions) ? debouncedConditions : null);

  // Between an edit and the debounce firing, the last result no longer describes the
  // tree on screen. Report that as loading rather than letting a stale count read as current.
  const isStale = serialised !== debouncedSerialised;
  const isLoading = preview.isFetching || isStale;

  return (
    <div className="flex flex-col gap-1.5">
      <ImpactPreviewBanner
        matchCount={preview.data?.matchCount ?? 0}
        totalCount={preview.data?.totalCount ?? 0}
        isLoading={isLoading}
      />
      {!isLoading && preview.data && (
        <p className="px-1 text-xs text-tertiary">
          <span className="font-medium text-secondary">{preview.data.willApply}</span> pending requests would actually
          be affected — excludes creators already exempt
        </p>
      )}
    </div>
  );
}
